import styles from "./BubblesGame.module.less";
import DownTime from "../components/DownTime";
import NoticeText from "../components/NoticeText";
import DSBridge from "dsbridge";
import { useState, useEffect, useRef, useCallback } from "react";
import { getGameAudio, getMaterialsByGame } from "@/api/mine";
import { showDeviceModal } from "views/mine/device-manage/components/device-modal";
import { Toast } from "react-vant";
import { useSearchParams } from "react-router-dom";
import {
  tSetTimeout,
  tClearAll,
  tOnPause,
  tOnResume,
  tAddGlobalListeners,
  tRemoveGlobalListeners,
} from "../components/TimestampTimer";

const BubblesGame = () => {
  const [searchParams] = useSearchParams();
  const [isVisible, setIsVisible] = useState(true);
  const [bubbleArray, setBubbleArray] = useState([]);
  const [visibleBubbles, setVisibleBubbles] = useState(new Set());
  const [disappearedBubbles, setDisappearedBubbles] = useState(new Set());

  const downTimeRef = useRef(null);
  const isStartRef = useRef(false);
  const explanationRef = useRef(null);
  const isOperateRef = useRef(false);
  const bubbleTimersRef = useRef([]);
  const pauseCleanupRef = useRef(null);
  const resumeCleanupRef = useRef(null);
  const isPausedRef = useRef(false);

  const pauseBubbleAnimations = useCallback(() => {
    console.log("🔄 暂停泡泡动画");
    isPausedRef.current = true;
    
    const bubbleElements = document.querySelectorAll(
      `.${styles["animated-bubble"]}`
    );
    bubbleElements.forEach((element) => {
      element.style.animationPlayState = "paused";
    });
  }, []);

  const resumeBubbleAnimations = useCallback(() => {
    console.log("▶️ 恢复泡泡动画");
    isPausedRef.current = false;

    const bubbleElements = document.querySelectorAll(
      `.${styles["animated-bubble"]}`
    );
    bubbleElements.forEach((element) => {
      element.style.animationPlayState = "running";
    });
  }, []);

  useEffect(() => {
    console.log("🔧 注册TimestampTimer回调和全局监听器");

    // 添加全局监听器
    tAddGlobalListeners();

    // 注册暂停/恢复回调
    pauseCleanupRef.current = tOnPause(pauseBubbleAnimations);
    resumeCleanupRef.current = tOnResume(resumeBubbleAnimations);

    return () => {
      // 清理回调
      if (pauseCleanupRef.current) {
        pauseCleanupRef.current();
      }
      if (resumeCleanupRef.current) {
        resumeCleanupRef.current();
      }
      
      // 移除全局监听器
      tRemoveGlobalListeners();
    };
  }, [pauseBubbleAnimations, resumeBubbleAnimations]);

  const handleClose = () => {
    showDeviceModal({
      message:
        "确定退出课堂练习？中途退出无法获得积分哦~退出后将继续学习课程。",
      onConfirm: () => {
        DSBridge.call("stopSound", "bgm_sound");
        DSBridge.call("exitWebView");
        tClearAll();
        tRemoveGlobalListeners(); // 确保退出时清理监听器
      },
      isShowCancel: true,
      isShowConfirm: true,
      cancelText: "取消",
      confirmText: "退出",
    });
  };

  const handleGetAudio = async (text) => {
    try {
      const res = await getGameAudio({
        character_id: searchParams.get("character_id"),
        token: searchParams.get("token"),
        text: text,
      });
      if (res.code === 200 && res.data?.url) {
        return res.data.url;
      }
      throw new Error("Failed to get audio URL");
    } catch (error) {
      throw error;
    }
  };

  const playAudioAndExit = async (explanationText, score = 0) => {
    try {
      const audioUrl = await handleGetAudio(explanationText);
      DSBridge.call(
        "playOnlineSound",
        `${process.env.REACT_APP_API_CDN}/${audioUrl}`,
        () => {
          if (score > 0) {
            DSBridge.call("addScore", score.toString());
          }
          DSBridge.call("exitWebView");
        }
      );
    } catch {
      if (score > 0) {
        DSBridge.call("addScore", score.toString());
      }
      DSBridge.call("exitWebView");
    }
  };

  const handleTimeEnd = () => {
    DSBridge.call("stopSound", "bgm_sound");
    isStartRef.current = false;
    tClearAll();

    if (disappearedBubbles.size === 5) {
      DSBridge.call("playSVGA", "outstanding", async () => {
        playAudioAndExit(explanationRef.current?.correct, 10);
      });
    } else if (isOperateRef.current === true) {
      DSBridge.call("playSVGA", "nice_try", async () => {
        playAudioAndExit(
          explanationRef.current?.incorrect,
          disappearedBubbles.size * 2
        );
      });
    } else {
      DSBridge.call("playSVGA", "missed", async () => {
        playAudioAndExit(explanationRef.current?.noAnswer);
      });
    }
  };

  const showBubble = useCallback((bubbleId) => {
    console.log(`显示泡泡: ${bubbleId}`);
    setVisibleBubbles((prev) => new Set([...prev, bubbleId]));
  }, []);

  const handleBubbleTouchStart = (e, bubble) => {
    if (
      disappearedBubbles.has(bubble.id) ||
      !isStartRef.current ||
      !visibleBubbles.has(bubble.id) ||
      isPausedRef.current
    ) {
      return;
    }
    isOperateRef.current = true;
    if (e.currentTarget) {
      e.currentTarget.style.transform = "scale(0.95)";
      e.currentTarget.style.transition = "transform 0.1s ease";
    }
  };

  const handleBubbleTouchEnd = (e, bubble) => {
    if (e.currentTarget) {
      e.currentTarget.style.transform = "";
      e.currentTarget.style.transition = "";
    }

    if (
      disappearedBubbles.has(bubble.id) ||
      !isStartRef.current ||
      !visibleBubbles.has(bubble.id) ||
      isPausedRef.current
    ) {
      return;
    }

    if (bubble.isCorrect) {
      setDisappearedBubbles((prev) => new Set([...prev, bubble.id]));
      console.log("正确答案！泡泡消失:", bubble.id);
      DSBridge.call("playSound", "correct_sound");
    } else {
      console.log("错误答案:", bubble.id);
      DSBridge.call("playSound", "aou_sound");
    }
  };

  const shuffleArray = (array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  const checkBubbleOverlap = (bubble1, bubble2, bubbleSize) => {
    const dx = bubble1.leftPosition - bubble2.leftPosition;
    const dt = Math.abs(bubble1.showDelay - bubble2.showDelay);

    const verticalSeparation = dt / 80;
    const horizontalDistance = Math.abs(dx);
    const minHorizontalDistance = bubbleSize * 1.2;
    const minVerticalSeparation = bubbleSize * 0.3;

    return (
      horizontalDistance < minHorizontalDistance &&
      verticalSeparation < minVerticalSeparation
    );
  };

  const generateNonOverlappingPosition = (
    existingBubbles,
    bubbleSize,
    duration,
    index,
    totalBubbles
  ) => {
    const bubbleWidthPercent = 23;
    const safeMargin = bubbleWidthPercent / 2;
    const leftBoundary = 10 + safeMargin;
    const rightBoundary = 90 - safeMargin;
    const availableWidth = rightBoundary - leftBoundary;

    let attempts = 0;
    const maxAttempts = 50;
    const singleBubbleAnimationDuration = 4;

    const maxStartDelay = Math.max(
      0,
      (duration - singleBubbleAnimationDuration) * 1000
    );
    const showDelay = (index / (totalBubbles - 1)) * maxStartDelay;

    while (attempts < maxAttempts) {
      const leftPosition = leftBoundary + Math.random() * availableWidth;
      const newBubble = { leftPosition, showDelay };

      let hasOverlap = false;
      for (const existingBubble of existingBubbles) {
        if (checkBubbleOverlap(newBubble, existingBubble, bubbleSize)) {
          hasOverlap = true;
          break;
        }
      }

      if (!hasOverlap) {
        return {
          leftPosition,
          showDelay,
          animationDuration: singleBubbleAnimationDuration * 1000,
        };
      }

      attempts++;
    }

    const fallbackPosition = leftBoundary + (index % 8) * (availableWidth / 8);

    return {
      leftPosition: fallbackPosition,
      showDelay,
      animationDuration: singleBubbleAnimationDuration * 1000,
    };
  };

  const generateBubbleArray = (bubbles, duration) => {
    const expandedBubbles = [];
    const bubbleSize = 23;
    const bubbleColors = ["blue-bubble", "green-bubble", "yellow-bubble"];
    const totalBubbles = bubbles.length * 5;

    bubbles.forEach((bubble, index) => {
      for (let i = 0; i < 5; i++) {
        const randomColorIndex = Math.floor(
          Math.random() * bubbleColors.length
        );
        const bubbleColor = bubbleColors[randomColorIndex];

        expandedBubbles.push({
          image: bubble.image,
          isCorrect: bubble.isCorrect,
          id: `${index}-${i}`,
          bubbleColor: bubbleColor,
        });
      }
    });

    const shuffledBubbles = shuffleArray(expandedBubbles);
    const finalBubbles = [];

    shuffledBubbles.forEach((bubble, index) => {
      const position = generateNonOverlappingPosition(
        finalBubbles,
        bubbleSize,
        duration,
        index,
        totalBubbles
      );

      finalBubbles.push({
        ...bubble,
        leftPosition: position.leftPosition,
        showDelay: position.showDelay,
        animationDuration: position.animationDuration,
      });
    });

    return finalBubbles;
  };

  const scheduleBubbleDisplay = useCallback(
    (bubbles) => {
      console.log("安排泡泡显示时机，总数:", bubbles.length);

      bubbleTimersRef.current = [];

      bubbles.forEach((bubble) => {
        const timerId = tSetTimeout(() => {
          if (isStartRef.current) {
            showBubble(bubble.id);
          }
        }, bubble.showDelay);

        bubbleTimersRef.current.push(timerId);
        console.log(`安排泡泡 ${bubble.id} 在 ${bubble.showDelay}ms 后显示`);
      });
    },
    [showBubble]
  );

  const initGame = async () => {
    setDisappearedBubbles(new Set());
    setVisibleBubbles(new Set());
    isPausedRef.current = false;
    bubbleTimersRef.current = [];

    try {
      // const res = {
      //   code: 200,
      //   status: true,
      //   data: {
      //     type: 14,
      //     question: "Let's start practicing.",
      //     question_zh: "",
      //     answer: "",
      //     image: "https://example.com/image.png",
      //     practice_config: {
      //       type: "bubble",
      //       guide:
      //         "Pop the bubble with the correct word. Can you find 'afternoon'",
      //       duration: 20,
      //       explanation: {
      //         correct:
      //           "Nice! You popped afternoon! That's the time after lunch.",
      //         incorrect:
      //           "Oops! The right word is afternoon. It means after lunch.",
      //         noAnswer:
      //           "The word was afternoon. It's the time of day after lunch.",
      //       },
      //       config: {
      //         bubbles: [
      //           {
      //             image: "https://example.com/image1.png",
      //             isCorrect: true,
      //           },
      //           {
      //             image: "https://example.com/image2.png",
      //             isCorrect: false,
      //           },
      //           {
      //             image: "https://example.com/image3.png",
      //             isCorrect: false,
      //           },
      //           {
      //             image: "https://example.com/image4.png",
      //             isCorrect: false,
      //           },
      //         ],
      //       },
      //     },
      //   },
      //   message: "success",
      // };
      const res = await getMaterialsByGame({
        material_id: searchParams.get("material_id"),
        token: searchParams.get("token"),
        unit_id: searchParams.get("unit_id"),
      });
      if (res.code === 200) {
        const config = res.data?.practice_config;
        if (config?.type !== "bubble") {
          Toast.info("游戏类型错误，请点击左上角退出");
          return;
        }

        explanationRef.current = config?.explanation;
        const bubbles = config?.config?.bubbles || [];
        const duration = config?.duration || 15;

        const generatedBubbleArray = generateBubbleArray(bubbles, duration);
        console.log("生成的泡泡数组:", generatedBubbleArray);

        setBubbleArray(generatedBubbleArray);

        try {
          const audioUrl = await handleGetAudio(config?.guide);
          DSBridge.call(
            "playOnlineSound",
            `${process.env.REACT_APP_API_CDN}/${audioUrl}`,
            () => {
              downTimeRef.current?.startCountdown(duration);
              isStartRef.current = true;
              scheduleBubbleDisplay(generatedBubbleArray);
            }
          );
        } catch {
          downTimeRef.current?.startCountdown(duration);
          isStartRef.current = true;
          scheduleBubbleDisplay(generatedBubbleArray);
        }
      } else {
        Toast.info("请求游戏数据失败，请点击左上角退出");
      }
    } catch {
      Toast.info("验证游戏数据失败，请点击左上角退出");
    }
  };

  useEffect(() => {
    DSBridge.call("playSound", "bgm_sound");
    DSBridge.call("playSound", "start_sound");

    tSetTimeout(() => {
      setIsVisible(false);
    }, 1000);

    initGame();

    return () => {
      DSBridge.call("stopSound", "bgm_sound");
      tClearAll();
    };
  }, []);

  return (
    <div className={styles["bubbles-game-container"]}>
      {isVisible && (
        <div className={styles["start-box"]}>
          <img
            src={require("@/images/new-game/common/start.png")}
            className={styles["start-cover"]}
          />
        </div>
      )}
      <div className={`${isVisible ? styles["visibility-box"] : ""}`}>
        <div className={styles["back-top"]}></div>
        <div className={styles["title"]}>
          <div className={styles["title-content"]}>
            {[1, 2, 3, 4, 5].map((item, index) => (
              <div
                className={`${styles["title-content-item"]} ${
                  disappearedBubbles.size > index
                    ? styles[`light--open`]
                    : styles[`light--close`]
                }`}
                key={index}
              ></div>
            ))}
          </div>
        </div>
        <div className={styles["back-bottom"]}></div>
        <DownTime ref={downTimeRef} onTimeEnd={handleTimeEnd} />
        <NoticeText text=" Click 听一听，点击正确的泡泡吧！" />
        {bubbleArray
          .filter(
            (bubble) =>
              !disappearedBubbles.has(bubble.id) &&
              visibleBubbles.has(bubble.id)
          )
          .map((bubble, index) => (
            <div
              className={`${styles["animated-bubble"]} ${
                styles[bubble.bubbleColor]
              }`}
              key={bubble.id}
              style={{
                left: `${bubble.leftPosition}%`,
                animationDuration: `${bubble.animationDuration}ms`,
              }}
              onTouchStart={(e) => handleBubbleTouchStart(e, bubble)}
              onTouchEnd={(e) => handleBubbleTouchEnd(e, bubble)}
            >
              <img className={styles["bubble-content"]} src={bubble.image} />
            </div>
          ))}
      </div>
      <div className={styles["close-icon"]} onClick={handleClose}>
        <img
          className={styles["close-icon-img"]}
          src={require("@/images/new-game/common/close.png")}
        />
      </div>
    </div>
  );
};

export default BubblesGame;
